import { ApiQueryName } from "@/models/api"
import { Meter } from "@/models/meter"
import type { PaginatedData } from "@/nzds/data-grid"
import { useSpraypaintInfiniteQuery } from "@/services/utils/spraypaint"
import type { HttpError } from "@/utils/api"
import { toHttpError } from "@/utils/api"
import type { UseInfiniteQueryOptions } from "@tanstack/react-query"
import type { CollectionProxy } from "spraypaint/lib-esm/proxies"

import type { SpraypaintQueryOptions } from "../types"
import { useRenameQueryResult } from "../utils"
import { createQueryKey } from "../utils/createQueryKey"

type MeterWithRequiredId = Meter & { id: number | string }
interface PageCountStats {
  number: number
  pages: number
  size: number
  total: number
}

interface CollectionMetaWithPageCount {
  stats?: {
    page?: {
      count?: PageCountStats
    }
  }
}

interface UseMetersInfiniteOptions {
  apiQueryOptions?: SpraypaintQueryOptions
  initialPage?: number
  queryOptions?: Omit<
    UseInfiniteQueryOptions<PaginatedData<MeterWithRequiredId>, HttpError>,
    | "queryKey"
    | "queryFn"
    | "getNextPageParam"
    | "getPreviousPageParam"
    | "initialPageParam"
    | "select"
  >
}

/** Max page size allowed by the API; requests for more return UnsupportedPageSize */
const maxPageSize = 1000
const defaultPageSize = 100

const getMeters = async (
  options: SpraypaintQueryOptions
): Promise<PaginatedData<MeterWithRequiredId>> => {
  const { extraFields, includes, page, pageSize, sort, where } = options
  const requestedPage = page ?? 1
  const requestedPageSize = Math.min(pageSize ?? defaultPageSize, maxPageSize)

  try {
    // Meter's recursive model typing can overflow TS in this hook; narrow after fetch.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: CollectionProxy<any> = await Meter.where(where ?? {})
      .includes(includes ?? [])
      .order(sort ?? { meter_code: "asc" })
      .page(requestedPage)
      .per(requestedPageSize)
      .selectExtra(extraFields ?? [])
      .all()

    const items = (response.data as { id?: unknown }[]).filter(
      (meter): meter is MeterWithRequiredId =>
        meter?.id !== null && meter?.id !== undefined
    )

    const stats = (response.meta as CollectionMetaWithPageCount | undefined)
      ?.stats?.page?.count

    return {
      items,
      page: stats?.number ?? requestedPage,
      pages:
        stats?.pages ??
        (items.length < requestedPageSize ? requestedPage : requestedPage + 1),
      size: stats?.size ?? requestedPageSize,
      total:
        stats?.total ?? items.length + (requestedPage - 1) * requestedPageSize,
    }
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

export const useMetersInfinite = ({
  apiQueryOptions = {},
  initialPage,
  queryOptions = {},
}: UseMetersInfiniteOptions = {}) => {
  // `infinite` keeps this key distinct from the single-page `useMeters` key.
  // Without it the two hooks collide whenever their options stringify alike --
  // `json-stable-stringify` drops undefined values, so a site-scoped
  // `useMeters` with `site_id: undefined` hashes identically to an
  // organization-wide `useMetersInfinite`, and the paged cache entry would be
  // handed to a caller expecting a flat `Meter[]`.
  // Invalidation matches on the first three key segments only, so meter and
  // site mutations still refresh both hooks.
  const queryKey = createQueryKey(ApiQueryName.Meters, "getMany", null, {
    apiQueryOptions,
    infinite: true,
  })

  const queryResult = useSpraypaintInfiniteQuery<MeterWithRequiredId>({
    apiQueryOptions,
    defaultPageSize,
    fetchPage: getMeters,
    initialPage,
    queryKey,
    queryOptions,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Meters)
}
